import { extractPlotly } from './graph';
import type { SelfHelpFeedbackMessageDescriptor } from '../interfaces/api';
import { SELF_HELP_FEEDBACK_CATEGORIES } from '../interfaces/selfHelpFeedback';

export interface Formatted {
  format: 'graph' | 'htmltable' | 'markdown' | 'text' | 'chart' | 'form' | string;
  dataToDisplay: Record<string, any>;
}

const tryParse = (v: any) => {
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v && typeof v === 'object' ? v : null;
};

const getSelfHelpFeedback = (raw: any): SelfHelpFeedbackMessageDescriptor | undefined => {
  const descriptor = raw?.selfHelpFeedback;
  if (
    !descriptor?.journeyId ||
    !descriptor?.selfHelpTurnId ||
    !descriptor?.validationContextId ||
    !descriptor?.guidanceReference ||
    !descriptor?.appName
  ) {
    return undefined;
  }
  const copy = descriptor.copy;
  const requiredCopyValues = [
    copy?.resolutionPrompt,
    copy?.resolvedLabel,
    copy?.unresolvedLabel,
    copy?.resolvedAcknowledgement,
    copy?.supportRequestHeader,
    copy?.supportRequestBody,
    copy?.feedbackHeading,
    copy?.feedbackPrompt,
    copy?.feedbackDetailPrompt,
    copy?.feedbackRequiredError,
    copy?.submitFeedbackLabel,
    copy?.resubmitFeedbackLabel,
    copy?.validationProgress,
    copy?.validatedAcknowledgement,
    copy?.technicalFallback,
    copy?.blockedTicketMessage,
    copy?.ticketActionLabel,
    copy?.ticketActionPrompt,
  ];
  const hasCompleteCategories = SELF_HELP_FEEDBACK_CATEGORIES.every(
    category =>
      typeof copy?.categoryLabels?.[category] === 'string' &&
      copy.categoryLabels[category].trim().length > 0
  );
  if (
    requiredCopyValues.some(
      value => typeof value !== 'string' || value.trim().length === 0
    ) ||
    !hasCompleteCategories
  ) {
    throw new Error('Self-help feedback descriptor copy is incomplete.');
  }

  return {
    ...descriptor,
    descriptorId: descriptor.journeyId,
    owningSelfHelpTurnId: descriptor.selfHelpTurnId,
  };
};

export const formatAndConvertByType = (raw: any): Formatted => {
  if (!raw) return { format: 'text', dataToDisplay: { markdownString: '' } };
  const selfHelpFeedback = getSelfHelpFeedback(raw);
  const feedbackMetadata = selfHelpFeedback ? { selfHelpFeedback } : {};
  
  const serverType: string | undefined = (raw.format || '').toString().toLowerCase() || undefined;

  
  if (serverType?.toLowerCase() === 'form' || serverType?.toLowerCase() === 'json') {
    if (raw.formJson && raw.formJson.fields) {
      return {
        format: 'form',
        dataToDisplay: {
          ...feedbackMetadata,
          formFields: raw.formJson.fields,
          formTitle: raw.formJson.formName || 'Dynamic Form',
          formDescription: raw.formJson.currentSubsectionDescription || '',          
          formJson: raw.formJson,
          markdownString: raw.response || '' 
        }
      };
    }
  }

  
  if (serverType && (serverType.toLowerCase().includes("chart") || serverType.toLowerCase() === "graph")) {
    
    if (raw.structuredData && typeof raw.structuredData === 'object') {
      return {
        format: 'graph',
        dataToDisplay: {
          ...feedbackMetadata,
          structuredData: raw.structuredData,
          markdownString: raw.response || '' 
        }
      };
    }
    
    
    if (raw.plotData || raw.data?.plotData) {
      return {
        format: 'graph',
        dataToDisplay: {
          ...feedbackMetadata,
          plotData: raw.plotData || raw.data.plotData,
          layout: raw.layout || raw.data.layout,
          markdownString: raw.response || ''
        }
      };
    }
    
    
    if (typeof raw.structuredData === 'string') {
      try {
        const chartConfig = JSON.parse(raw.structuredData);
        return {
          format: 'graph',
          dataToDisplay: {
            ...feedbackMetadata,
            plotData: chartConfig.data,
            layout: chartConfig.layout,
            markdownString: raw.response || ''
          }
        };
      } catch (error) {
        console.warn('Failed to parse structuredData JSON:', error);
      }
    }
  }

  // Table detection: explicit fields, structured data, or html snippet in reply
  //const tableHtml = raw.tableHtml || raw.table || (typeof raw.reply === 'string' && raw.reply.includes('<table') ? raw.reply : undefined);
  const structuredData = raw.structuredData;

  if (serverType && serverType?.includes('table')) {
    // Handle new API format with structured data
   // if (structuredData && structuredData.columns && structuredData.rows) {
      return {
        format: 'table',
        dataToDisplay: {
          ...feedbackMetadata,
          columns: structuredData.columns,
          rows: structuredData.rows,
          responseText: raw.response || '',
          suppressTextContent: raw.suppressTextContent || false,          
        }
      };
    //}
    
    // Fallback to legacy HTML table format
    // return {
    //   format: 'table',
    //   dataToDisplay: {
    //    // tableHtml: tableHtml,
    //    // note: raw.note || raw.tableNote,
    //     responseText: raw.response || ''
    //   }
    // };
  }

  
  if (serverType === 'text') {
    const textResponse = raw.response || raw.reply || raw.answer || raw.result || '';
    return {
      format: 'text',
      dataToDisplay: { 
        ...feedbackMetadata,
        markdownString: typeof textResponse === 'string' ? textResponse : String(textResponse)
      }
    };
  }

  
  const parsedData = tryParse(raw) || raw;
  const mdFromKnown = parsedData?.markDownResult
    ?? parsedData?.data?.markDownResult
    ?? raw?.markDownResult
    ?? raw?.data?.markDownResult;
  const reply = mdFromKnown ?? parsedData?.reply ?? raw?.answer ?? raw?.response ?? raw?.result ?? '';
  
  
  let markdownString = '';
  if (typeof reply === 'string') {
    markdownString = reply.replace(/<\/?Cell>/g, '');
  } else if (reply !== null && reply !== undefined) {
    
    markdownString = String(reply);
  }
  
  return {
    format: 'markdown',
    dataToDisplay: { ...feedbackMetadata, markdownString }
  };
};

// Generated by Copilot
// updated on: 2026-09-09 | Story 1297509 TASK-005 | Preserved typed self-help feedback descriptors during message formatting | Time Taken 3 min
// updated on: 2026-09-10 | Story 1297509 hardening | Rejected incomplete descriptors instead of applying UI fallback copy | Time Taken 3 min
// updated on: 2026-09-13 | 5 lines changed | [Auto Generated] | Require server-issued validation context metadata | Time Taken 2 min
